import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home as HomeIcon,
  ShowChart as ChartIcon,
  Favorite as FavoriteIcon,
  AccountCircle as ProfileIcon,
  Search as SearchIcon,
  OnlinePrediction as PredictionIcon,
  ModelTraining as BrainIcon,
  Notifications as AlertsIcon,
  Timeline as TimelineIcon,
  Bookmark as BookmarkIcon,
  AdminPanelSettings as AdminIcon,
  Scanner as ScannerIcon,
  Assessment as AssessmentIcon,
  FlashOn as FlashIcon,
  Psychology as PsychologyIcon,
  Radar as RadarIcon,
  Campaign as CampaignIcon,
  EventRepeat as SwingIcon,
  Speed as MomentumIcon,
  CompareArrows as WhipsawIcon
} from '@mui/icons-material';
import { NavigationItem } from '../../types';
import { useAuth } from '../../context/AuthContext';

const baseNavigationItems: NavigationItem[] = [
  { label: 'Home', path: '/', icon: 'HomeIcon' },
  { label: 'Markets', path: '/markets', icon: 'ChartIcon' },
  { label: 'Watchlist', path: '/watchlist', icon: 'FavoriteIcon' },
  { label: 'Train Model', path: '/training', icon: 'BrainIcon' },
  { label: 'Enhanced Prediction', path: '/enhanced-prediction', icon: 'PredictionIcon' },
  { label: 'Pro Scanner', path: '/pro-scanner', icon: 'ScannerIcon' },
  { label: 'Market Scanner AI', path: '/market-scanner', icon: 'RadarIcon' },
  { label: 'Day Trading AI', path: '/day-trading', icon: 'AssessmentIcon' },
  { label: 'Swing Trader AI', path: '/swing-trader', icon: 'SwingIcon' },
  { label: 'Momentum Trend AI', path: '/momentum-trader', icon: 'MomentumIcon' },
  { label: 'True Scalper AI', path: '/scalper', icon: 'FlashIcon' },
  { label: 'Whipsaw AI', path: '/whipsaw-trader', icon: 'WhipsawIcon' },
  { label: 'Pullbacks', path: '/pullbacks', icon: 'TimelineIcon' },
  { label: 'Alerts', path: '/alerts', icon: 'AlertsIcon' },
  { label: 'Trade Dashboard', path: '/recorded-trades', icon: 'BookmarkIcon' },
  { label: 'Job Scout AI', path: '/job-scout', icon: 'SearchIcon' },
  { label: 'Community Feed', path: '/community', icon: 'CampaignIcon' },
  { label: 'Profile', path: '/profile', icon: 'ProfileIcon' }
];

const getIcon = (iconName: string) => {
  const iconProps = { className: 'h-5 w-5 mr-3 flex-shrink-0' };
  
  switch (iconName) { 
    case 'HomeIcon':
      return <HomeIcon {...iconProps} />;
    case 'ChartIcon':
      return <ChartIcon {...iconProps} />;
    case 'FavoriteIcon':
      return <FavoriteIcon {...iconProps} />;
    case 'PredictionIcon':
      return <PredictionIcon {...iconProps} />;
    case 'BrainIcon':
      return <BrainIcon {...iconProps} />;
    case 'ScannerIcon':
      return <ScannerIcon {...iconProps} />;
    case 'AssessmentIcon':
      return <AssessmentIcon {...iconProps} />;
    case 'SwingIcon':
      return <SwingIcon {...iconProps} />;
    case 'MomentumIcon':
      return <MomentumIcon {...iconProps} />;
    case 'FlashIcon':
      return <FlashIcon {...iconProps} />;
    case 'WhipsawIcon':
      return <WhipsawIcon {...iconProps} />;
    case 'PsychologyIcon':
      return <PsychologyIcon {...iconProps} />;
    case 'TimelineIcon':
      return <TimelineIcon {...iconProps} />;
    case 'AlertsIcon':
      return <AlertsIcon {...iconProps} />;
    case 'BookmarkIcon':
      return <BookmarkIcon {...iconProps} />;
    case 'ProfileIcon':
      return <ProfileIcon {...iconProps} />;
    case 'AdminIcon':
      return <AdminIcon {...iconProps} />;
    case 'RadarIcon':
      return <RadarIcon {...iconProps} />;
    case 'SearchIcon':
      return <SearchIcon {...iconProps} />;
    case 'CampaignIcon':
      return <CampaignIcon {...iconProps} />;
    default:
      return null;
  }
};

interface MobileNavigationProps {
  onLinkClick: () => void;
}

export const MobileNavigation: React.FC<MobileNavigationProps> = ({ onLinkClick }) => {
  const location = useLocation();
  const { user } = useAuth();

  const navigationItems = [...baseNavigationItems];
  if (user?.role === 'admin') {
    navigationItems.push({ label: 'Admin', path: '/admin/blue-chip', icon: 'AdminIcon' });
  }

  return (
    <nav className="h-full bg-white dark:bg-dark-paper flex flex-col shadow-lg">
      <div className="flex items-center h-16 px-6 border-b border-gray-200 dark:border-dark-border">
        <h1 className="text-xl font-bold text-primary dark:text-white">Stockit</h1>
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1">
          {navigationItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={onLinkClick}
              className={`group flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                location.pathname === item.path
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-dark-text-secondary dark:hover:bg-dark-hover'
              }`}
            >
              {getIcon(item.icon)}
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
};

export const DesktopNavigation: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();

  const navigationItems = [...baseNavigationItems];
  if (user?.role === 'admin') {
    navigationItems.push({ label: 'Admin', path: '/admin/blue-chip', icon: 'AdminIcon' });
  }

  return (
    <nav className="flex-1 py-4">
      <div className="space-y-1">
        {navigationItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`group flex items-center px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              location.pathname === item.path
                ? 'bg-primary/10 text-primary'
                : 'text-gray-700 hover:bg-gray-50 dark:text-dark-text-secondary dark:hover:bg-dark-hover'
            }`}
          >
            {getIcon(item.icon)}
            <span>{item.label}</span>
          </Link>
        ))}
      </div>
    </nav>
  );
};
